import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, Phone, Mail, MapPin, ChevronDown } from "lucide-react";
import { SITE } from "../lib/site";
import { getAttribution } from "../lib/attribution";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xredrzld";

const SERVICES = [
  "Daily Corporate Meals",
  "Event Catering",
  "Packed Bento Boxes",
  "Pantry Management",
  "Other",
];

const initialForm = {
  name: "",
  company: "",
  email: "",
  phone: "",
  service_type: "Daily Corporate Meals",
  event_date: "",
  guest_count: "",
  message: "",
};

const FAQS = [
  {
    question: "How much does corporate catering cost in Bangalore?",
    answer: "Our corporate catering starts from ₹120 per person and varies based on menu selection, volume, and service type. Daily meal plans, event catering, and bento boxes have different pricing tiers. Contact us for a customized quote based on your team size and requirements."
  },
  {
    question: "Which areas in Bangalore do you serve?",
    answer: "We serve pan Bangalore with easy access to HSR Layout, Electronic City, Bellandur, Marathahalli, JP Nagar, Bannerghatta Road, Koramangala, Whitefield, Manyata Tech Park, and Outer Ring Road. We can cater to offices across all major tech corridors in Bengaluru."
  },
  {
    question: "What is the minimum order size for daily office meals?",
    answer: "We typically cater to teams of 20+ people for daily meal plans. However, we can accommodate smaller teams for event catering and bento box orders. Contact us to discuss your specific requirements."
  },
  {
    question: "Do you provide vegetarian and special dietary meals?",
    answer: "Yes, we offer a wide range of options including vegetarian, Jain, vegan, and gluten-free meals. Our menu planning includes dietary preference management for diverse teams. We can customize menus based on your team's specific needs."
  },
  {
    question: "How far in advance should I book event catering?",
    answer: "For event catering, we recommend booking at least 3–5 days in advance for smaller events (under 100 guests) and 1–2 weeks for larger corporate events. For daily meal plans, we can start within 2–3 business days after finalizing the menu."
  },
  {
    question: "What is included in daily corporate meal plans?",
    answer: "Our daily corporate meal plans can include breakfast, lunch, evening snacks, and dinner. Each meal is freshly prepared in our Central kitchen and delivered hot to your office. We also provide cutlery, napkins, and serving equipment as needed."
  }
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#1E3A5F]/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="font-serif text-lg text-[#1E3A5F] pr-4">{question}</span>
        <ChevronDown 
          size={20} 
          className={`text-[#CBA365] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} 
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-96 pb-5' : 'max-h-0'}`}
      >
        <p className="text-[#1E3A5F]/75 font-light leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone) {
      toast.error("Please fill in your name, company, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const payload = {
        ...form,
        ...attribution,
        _subject: `New Catering Enquiry from ${form.company}`,
        _replyto: form.email,
      };
      
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
if (typeof window.gtag === "function") {
    window.gtag("event", "generate_lead", {
      event_category: "lead",
      lead_source: "website_enquiry_form",
      service_type: form.service_type,
      page_path: window.location.pathname,
    });
  }
        toast.success("Thank you — we'll be in touch within one business day.");
        setForm(initialForm);
      } else {
        throw new Error("Form submission failed");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Contact Section */}
      <section
        id="contact"
        data-testid="contact-section"
        className="py-28 md:py-36 bg-[#FDFBF7]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-x-4 gap-y-10 md:gap-10 lg:gap-20">
            {/* Left intro + contact info */}
            <div className="col-span-12 lg:col-span-5">
              <p className="label-eyebrow">09 · Enquire</p>
              <h2 className="font-serif text-[#1E3A5F] mt-5 text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                Let's design
                <br />
                <span className="italic text-[#CBA365]">your next meal.</span>
              </h2>
              <p className="mt-6 text-[#1E3A5F]/75 font-light leading-relaxed max-w-md">
                Share a few details and a relationship chef will call you back
                within one business day with a bespoke proposal.
              </p>

              <div className="mt-12 space-y-6">
                <a
                  href={`tel:+${SITE.phoneRaw}`}
onClick={() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "phone_click", {
        event_category: "lead",
        event_label: "contact_section",
        page_path: window.location.pathname,
      });
    }
  }}
                  data-testid="contact-phone"
                  className="group flex items-start gap-4"
                >
                  <span className="mt-1 w-10 h-10 flex items-center justify-center border border-[#1E3A5F]/25 text-[#1E3A5F] group-hover:bg-[#1E3A5F] group-hover:text-[#FDFBF7] transition-all">
                    <Phone size={16} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B]">
                      Call
                    </span>
                    <span className="block font-serif text-xl text-[#1E3A5F]">
                      {SITE.phoneDisplay}
                    </span>
                  </span>
                </a>

                <a
                  href={`mailto:${SITE.email}`}
onClick={() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "email_click", {
        event_category: "lead",
        event_label: "contact_section",
        page_path: window.location.pathname,
      });
    }
  }}
                  data-testid="contact-email"
                  className="group flex items-start gap-4"
                >
                  <span className="mt-1 w-10 h-10 flex items-center justify-center border border-[#1E3A5F]/25 text-[#1E3A5F] group-hover:bg-[#1E3A5F] group-hover:text-[#FDFBF7] transition-all">
                    <Mail size={16} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B]">
                      Email
                    </span>
                    <span className="block font-serif text-xl text-[#1E3A5F] break-all">
                      {SITE.email}
                    </span>
                  </span>
                </a>

                <div
                  data-testid="contact-address"
                  className="flex items-start gap-4"
                >
                  <span className="mt-1 w-10 h-10 flex items-center justify-center border border-[#1E3A5F]/25 text-[#1E3A5F]">
                    <MapPin size={16} />
                  </span>
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B]">
                      Kitchen
                    </span>
                    <span className="block font-serif text-lg text-[#1E3A5F] leading-snug max-w-xs">
                      {SITE.address}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmit}
              data-testid="quote-form"
              className="col-span-12 lg:col-span-7 bg-[#F3EFE6]/50 border border-[#1E3A5F]/10 p-8 md:p-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    data-testid="quote-input-name"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1">
                    Company *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Acme Technologies"
                    data-testid="quote-input-company"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1 mt-6">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@acme.com"
                    data-testid="quote-input-email"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1 mt-6">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98xxxxxxxx"
                    data-testid="quote-input-phone"
                    className="input-minimal"
                  />
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1 mt-6">
                    Service
                  </label>
                  <select
                    value={form.service_type}
                    onChange={update("service_type")}
                    data-testid="quote-input-service"
                    className="input-minimal bg-transparent appearance-none cursor-pointer"
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1 mt-6">
                    Approx. Guests
                  </label>
                  <input
                    type="text"
                    value={form.guest_count}
                    onChange={update("guest_count")}
                    placeholder="e.g. 150"
                    data-testid="quote-input-guests"
                    className="input-minimal"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1 mt-6">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={update("event_date")}
                    data-testid="quote-input-date"
                    className="input-minimal"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.22em] text-[#64748B] mb-1 mt-6">
                    Tell us about your event
                  </label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder="Cuisine preferences, dietary notes, venue, anything we should know…"
                    data-testid="quote-input-message"
                    className="input-minimal resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-button"
                className="mt-10 group inline-flex items-center gap-3 bg-[#1E3A5F] text-[#FDFBF7] px-10 py-4 text-xs uppercase tracking-[0.24em] hover:bg-[#122644] transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <ArrowRight
                      size={16}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </>
                )}
              </button>
              <p className="mt-4 text-xs text-[#64748B] font-light">
                We respond to every enquiry within one business day.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="label-eyebrow text-center">10 · FAQ</p>
          <h2 className="font-serif text-[#1E3A5F] mt-5 text-3xl md:text-4xl lg:text-5xl leading-[1.02] text-center">
            Questions? <span className="italic text-[#CBA365]">We've got answers.</span>
          </h2>
          <p className="mt-4 text-[#1E3A5F]/75 font-light leading-relaxed text-center max-w-xl mx-auto">
            Everything you need to know about our corporate catering services in Bangalore.
          </p>
          
          <div className="mt-12">
            {FAQS.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-[#1E3A5F]/75 font-light">
              Still have questions?{" "}
              <a href={`tel:+${SITE.phoneRaw}`} className="text-[#CBA365] hover:underline">
                Call us
              </a>{" "}
              or{" "}
              <a href={`mailto:${SITE.email}`} className="text-[#CBA365] hover:underline">
                email us
              </a>{" "}
              — we're happy to help.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}